import { useCallback, useEffect, useState } from "react";
import { fetchCurrentUser } from "../services/auth";
import type { AuthUser, WosRole } from "../types/auth";

export interface UseAuthUserResult {
  user: AuthUser | null;
  loading: boolean;
  hasRole: (role: WosRole) => boolean;
  hasAnyRole: (...roles: WosRole[]) => boolean;
  hasFeature: (feature: string) => boolean;
  refresh: (silent?: boolean) => Promise<void>;
}

/**
 * Carrega `AuthUser` da sessão BFF via `/api/auth/me` (cookie HttpOnly).
 * SPEC-20260505-1712: substitui leitura de sessionStorage do mock-login.
 *
 * Re-fetch quando aba volta ao foco (cobre cookie expirar/refresh em outra aba)
 * e expõe `loading` pra UI mostrar skeleton inicial.
 */
export function useAuthUser(): UseAuthUserResult {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * `silent=true`: refresh em background — NÃO toca em `loading`.
   * Necessário pra evitar que RequireAuth remonte toda a tree filha (perdendo
   * state de formulários em edição) toda vez que a aba volta ao foco.
   */
  const refresh = useCallback(async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    try {
      const u = await fetchCurrentUser();
      setUser(u);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // Re-fetch silencioso quando aba volta ao foco — cobre caso do cookie
    // ter expirado/sido refrescado em outra aba. NÃO mostra spinner pra não
    // remontar a tree e perder state de formulários em edição.
    const onFocus = () => {
      void refresh(true);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const hasRole = (role: WosRole) => !!user && user.roles.includes(role);
  const hasAnyRole = (...roles: WosRole[]) =>
    !!user && roles.some((r) => user.roles.includes(r));
  const hasFeature = (feature: string) =>
    !!user && user.features.includes(feature);

  return { user, loading, hasRole, hasAnyRole, hasFeature, refresh };
}
